import { type ReactNode } from 'react'

/* ==========================================================================
   Card Component
   Based on design.md specifications
   ========================================================================== */

export interface CardProps {
  /** Card content */
  children: ReactNode
  /** Optional header text */
  header?: string
  /** Optional header with custom content */
  headerContent?: ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * Card container with white background, shadow, and optional header.
 * Responsive padding: 24px on desktop, 16px on mobile.
 *
 * @example
 * ```tsx
 * <Card>Simple content</Card>
 *
 * <Card header="Section Title">
 *   Content with header
 * </Card>
 *
 * <Card headerContent={<div>Custom header</div>}>
 *   Content with custom header
 * </Card>
 * ```
 */
export function Card({
  children,
  header,
  headerContent,
  className = '',
}: CardProps) {
  const hasHeader = header || headerContent

  return (
    <div
      className={`bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6 ${className}`}
    >
      {hasHeader && (
        <div className="text-lg font-semibold text-charcoal mb-4 pb-4 border-b border-gray-200">
          {headerContent ?? header}
        </div>
      )}
      {children}
    </div>
  )
}

export default Card
